import React, { type SyntheticEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { styled, Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';

import { useGetProjectById } from '../../hooks';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import { type ModelType } from '../../types';

import ModelConfigurationView from './ModelConfigurationView';
import ModelOverview from './ModelOverview';
import ModelScenarioOverview from './ModelScenarioOverview';
import SharedModelSharingInvitationsView from './SharedModelSharingInvitationsView';
import SharedModelsHistoryView from './SharedModelsHistoryView';

type TabPanelProps = {
  children?: React.ReactNode;
  index: string;
  value: string;
};

type StyledTabsProps = {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
};

type StyledTabProps = {
  label: string;
  id: string;
};

const Tabs = styled((props: StyledTabsProps) => (
  <MuiTabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))({
  gap: '12px !important',
  backgroundColor: '#F5F9FF',

  '& .MuiTabs-indicator': {
    display: 'flex',
    gap: '12px !important',
    justifyContent: 'center',
    backgroundColor: '#F5F9FF',
  },

  '& .MuiTabs-flexContainer': {
    paddingLeft: '48px',
    gap: '24px',
  },

  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: '#01285F',
  },
});

const Tab = styled((props: StyledTabProps) => (
  <MuiTab disableRipple {...props} />
))({
  fontFamily: 'Poppins, sans-serif',
  textTransform: 'none',
  padding: '0px !important',
  minWidth: 'fit-content !important',

  '&.MuiButtonBase-root': {
    padding: '0px !important',
  },
  '&.Mui-selected': {
    color: '#01285F',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent',
  },
});

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      className="space-y-6 px-12 py-6"
      role="tabpanel"
      hidden={value !== index}
      id={value}
      {...other}
    >
      {value === index && children}
    </div>
  );
};

export const TABS = ({
  type,
  is_base,
  is_guest_project,
  is_locked,
}: {
  type: ModelType;
  is_base?: boolean;
  is_guest_project?: boolean;
  is_locked?: boolean;
}) => {
  if (
    type === 'shared' ||
    type === 'received' ||
    is_base ||
    (is_guest_project && is_locked)
  ) {
    return ['Overview', 'Scenario & Expense', 'Configuration'];
  }

  return ['Overview', 'Scenario & Expense', 'Configuration', 'Shared History'];
};

const ReportEnum = () => ({
  overview: 0,
  scenario: 1,
  configuration: 2,
  sharedHistory: 3,
});

const ModelView = () => {
  const [index, setIndex] = useState(NaN);

  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get('tab');
  const type = searchParams.get('type') as ModelType;

  const { orgId, projectId } = useGetRouteParams();

  const { data: project } = useGetProjectById(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const { isShared, isDefault, model } = useGetDistinguishModel();

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setIndex(newValue);

    if (newValue === 0) {
      searchParams.set('tab', 'overview');
    } else if (newValue === 1) {
      searchParams.set('tab', 'scenario');
    } else if (newValue === 2) {
      searchParams.set('tab', 'configuration');
    } else if (newValue === 3) {
      if (isShared) {
        searchParams.set('tab', 'invitations');
        return;
      }

      searchParams.set('tab', 'sharedHistory');
    }

    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (!tab) {
      setIndex(0);
      return;
    }

    setIndex((ReportEnum() as unknown as Record<string, number>)[tab]);
  }, [tab, type, ReportEnum]);

  return (
    <>
      <Tabs value={index} onChange={handleChange}>
        {TABS({
          type,
          is_base: model?.is_base,
          is_guest_project: project?.is_guest_project,
          is_locked: model?.is_locked,
        }).map((report) => (
          <Tab id={report} key={report} label={report} />
        ))}
      </Tabs>
      <CustomTabPanel value={index.toString()} index="0">
        <ModelOverview />
      </CustomTabPanel>
      <CustomTabPanel value={index.toString()} index="1">
        <ModelScenarioOverview />
      </CustomTabPanel>
      <CustomTabPanel value={index.toString()} index="2">
        <ModelConfigurationView />
      </CustomTabPanel>
      <CustomTabPanel value={index.toString()} index="3">
        {isShared && <SharedModelSharingInvitationsView />}
        {isDefault && <SharedModelsHistoryView />}
      </CustomTabPanel>
    </>
  );
};

export default ModelView;
